import { trackAnonEvent } from '@/services/analytics/track-anon-event';

/**
 * The signup funnel's analytics catalog.
 *
 * ⚠️ Downstream dashboards key on these exact event names and property keys
 * (including the Title Case ones) — they must stay byte-identical. The full
 * catalog ships here so the step PRs never have to add names; `analytics.test.ts`
 * pins every name and builder output.
 */
export const SignupAnalyticsEvent = {
  Started: 'Signup: Started',
  NameAndEmailSubmitted: 'Signup: Name and Email Submitted',
  PasswordViewed: 'Signup: Password Viewed',
  PasswordSubmitted: 'Signup: Password Submitted',
  CompanyInfoViewed: 'Signup: Company Info Viewed',
  CompanyInfoSubmitted: 'Signup: Company Info and Terms Submitted',
  StartupStepViewed: 'Signup: Startup Step Viewed',
  StartupStepSubmitted: 'Signup: Startup Step Submitted',
  StartupCompleteViewed: 'Signup: Startup Complete Viewed',
  StartupCompleteSubmitted: 'Signup: Startup Complete Submitted',
  OnboardingUsageViewed: 'Signup: Onboarding Usage Viewed',
  OnboardingUsageSubmitted: 'Signup: Onboarding Usage Submitted',
  OnboardingPersonalizeSubmitted: 'Signup: Onboarding Personalize Submitted',
  ChannelsViewed: 'Signup: Channels Viewed',
  ChannelsSubmitted: 'Signup: Channels Submitted',
  InviteTeamMembersViewed: 'Signup: Invite Team Member Viewed',
  InviteTeamMembersInvited: 'Signup: Invite Team Member Invited',
  InviteTeamMembersSkipped: 'Signup: Invite Team Member Skipped',
  VerifyEmailViewed: 'Signup: Verify Email Viewed',
  VerificationCodeConfirmed: 'Signup: Verification Code Confirmed',
  VerificationCodeResent: 'Signup: Verification Code Sent Again',
  JoinRequestRedirect: 'Signup: Join Request Redirect',
  ResendConfirmationClicked: 'Signup: Resend Confirmation Clicked',
  SignInClicked: 'Signup: Sign in Clicked',
  /**
   * The `User:` prefix and "Sign **up**" wording are both deliberate — this is
   * a distinct event from login's `User: Sign in With Google Clicked`.
   */
  SignUpWithGoogleClicked: 'User: Sign up With Google Clicked',
  InboxLinkClicked: 'Signup: Inbox Link Clicked',
  /**
   * Fires post-auth (keyed on the account becoming ready) — NOT from the funnel.
   * Shipped for completeness; wiring is deferred to the migration cutover,
   * which owns the post-auth account-readiness story.
   */
  SignupCompleted: 'Signup: Completed',
} as const;

export type SignupAnalyticsEventName = (typeof SignupAnalyticsEvent)[keyof typeof SignupAnalyticsEvent];

/** Fire a signup funnel event to the pre-auth `track_anon` endpoint. */
export function trackSignupEvent(event: SignupAnalyticsEventName, properties: Record<string, unknown> = {}): void {
  trackAnonEvent(event, properties);
}

// ——— Property builders (one per transform) ———

export function signupTypeProps(args: { signupType: string | undefined; isGoogleSignup?: boolean }) {
  return {
    'Signup Type': args.signupType,
    'Is Google Signup': args.isGoogleSignup,
  };
}

export function verificationSubmittedProps(args: {
  confirmed: boolean;
  status?: number | string;
  signupType: string | undefined;
}) {
  return {
    Confirmed: args.confirmed,
    Status: args.status ?? 200,
    'Signup Type': args.signupType,
  };
}

export function teamMemberInvitedProps(args: { email: string; role: string; signupType: string | undefined }) {
  return {
    email: args.email,
    role: args.role,
    'Signup Type': args.signupType,
  };
}

export function inboxLinkClickedProps(args: { provider: string; emailDomain?: string }) {
  return {
    'MX Provider': args.provider,
    'Email Domain': args.emailDomain,
  };
}

export function companyInfoSubmittedProps(args: {
  signupType: string | undefined;
  companyName?: string;
  website?: string;
  haveWebsite?: boolean;
  role?: string | null;
}) {
  return {
    'Signup Type': args.signupType,
    ...(args.companyName !== undefined && { company_name: args.companyName }),
    ...(args.website !== undefined && { website: args.website }),
    ...(args.haveWebsite !== undefined && { have_website: args.haveWebsite }),
    ...(args.role != null && { role: args.role }),
  };
}

export function onboardingUsageSubmittedProps(args: {
  signupType: string | undefined;
  usage?: string[];
  dataCenter?: string;
  resolvedSignupType?: string;
}) {
  return {
    'Signup Type': args.signupType,
    usage: args.usage,
    data_center: args.dataCenter,
    resolved_signup_type: args.resolvedSignupType,
  };
}

/** Shared by `ChannelsSubmitted` (and the unported personalize step). */
export function onboardingPersonalizeSubmittedProps(args: {
  signupType: string | undefined;
  commsOptIn?: string[];
  channels?: string[];
  estimatedEmailVolume?: string | null;
  resolvedSignupType?: string;
}) {
  return {
    'Signup Type': args.signupType,
    comms_opt_in: args.commsOptIn,
    channels: args.channels,
    estimated_email_volume: args.estimatedEmailVolume ?? undefined,
    resolved_signup_type: args.resolvedSignupType,
  };
}

export function startupCompleteViewedProps(args: { signupType: string | undefined; resolvedSignupType?: string }) {
  return {
    'Signup Type': args.signupType,
    resolved_signup_type: args.resolvedSignupType,
  };
}

export function startupCompleteSubmittedProps(args: {
  signupType: string | undefined;
  isGoogleSignup?: boolean;
  resolvedSignupType?: string;
}) {
  return {
    'Signup Type': args.signupType,
    'Is Google Signup': args.isGoogleSignup,
    resolved_signup_type: args.resolvedSignupType,
  };
}

export function signupCompletedProps(args: {
  signupType: string | undefined;
  isGoogleSignup?: boolean;
  isStartup?: boolean;
}) {
  return {
    'Signup Type': args.signupType,
    'Is Google Signup': args.isGoogleSignup,
    'Is Startup': args.isStartup,
  };
}
